import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarCheck, Mail, Calendar as CalendarIcon } from 'lucide-react';
import GlassButton from '../ui/GlassButton';
import { bookingApi } from '../../services/api';
import './Booking.css';

type PageState = 'loading' | 'error' | 'loaded';

const CalendarConnectPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const [startingProvider, setStartingProvider] = useState<'ms' | 'google' | null>(null);
  const [startError, setStartError] = useState('');

  const load = useCallback(async () => {
    if (!token) {
      setErrorMsg('This link is no longer valid or has expired.');
      setPageState('error');
      return;
    }
    try {
      const info = await bookingApi.publicConnectInfo(token);
      setResourceName(info.resource_name);
      setProviders(info.providers);
      setConnected(info.connected);
      setPageState('loaded');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setErrorMsg('This link is no longer valid or has expired.');
      } else {
        setErrorMsg('Failed to load this page. Please try again.');
      }
      setPageState('error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async (provider: 'ms' | 'google') => {
    if (!token) return;
    setStartingProvider(provider);
    setStartError('');
    try {
      const res = await bookingApi.publicConnectStart(token, provider);
      window.location.href = res.auth_url;
    } catch (err: any) {
      setStartError(
        err?.response?.data?.detail ?? 'Could not start the connection. Please try again.'
      );
      setStartingProvider(null);
    }
  };

  // ─ Loading ─
  if (pageState === 'loading') {
    return (
      <div className="bk-page">
        <div className="bk-card">
          <p className="bk-desc" style={{ textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // ─ Error ─
  if (pageState === 'error') {
    return (
      <div className="bk-page">
        <div className="bk-card">
          <h1 className="bk-title">Link unavailable</h1>
          <p className="bk-desc">{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ─ Loaded ─
  return (
    <div className="bk-page">
      <div className="bk-card">
        <div className="bk-success-icon">
          <CalendarCheck size={52} />
        </div>
        <h1 className="bk-title">Connect your calendar</h1>
        {resourceName && <p className="bk-subtitle">for {resourceName}</p>}
        <p className="bk-desc">
          Connect your calendar so we can check your availability and add bookings automatically.
        </p>

        {connected && (
          <p className="bk-desc" style={{ color: 'var(--success, #16a34a)', textAlign: 'center' }}>
            Your calendar is already connected. You can reconnect below if needed.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          {providers.includes('ms') && (
            <GlassButton
              variant="primary"
              onClick={() => handleConnect('ms')}
              disabled={startingProvider !== null}
              style={{ width: '100%' }}
            >
              <Mail size={16} /> {startingProvider === 'ms' ? 'Redirecting...' : 'Connect Microsoft Outlook'}
            </GlassButton>
          )}
          {providers.includes('google') && (
            <GlassButton
              variant="secondary"
              onClick={() => handleConnect('google')}
              disabled={startingProvider !== null}
              style={{ width: '100%' }}
            >
              <CalendarIcon size={16} /> {startingProvider === 'google' ? 'Redirecting...' : 'Connect Google Calendar'}
            </GlassButton>
          )}
        </div>

        {startError && <p className="bk-error" style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>{startError}</p>}
      </div>
    </div>
  );
};

export default CalendarConnectPage;
